import React, { lazy, MutableRefObject, useCallback, useMemo, useState } from 'react'
import { AeronetViewProps } from '../types'
import { GridApi, GridColDef, GridFilterModel, GridSortItem, GridValueFormatterParams } from '@mui/x-data-grid-pro'
import  AeronetDataGrid, { QueryResults }  from '../components/datagrid/DataGrid'
import { CacheRequestConfig } from 'axios-cache-interceptor'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { AxiosResponse } from 'axios'
import { axios } from '../api'
import queryString from 'query-string'
import { useSnackbar } from 'notistack'
import { DueDateDeletePayload, DueDateList } from './type'
import { useConfig } from '../hooks/useConfig'
import { whiteboardStatusLabel, whiteboardStatusOptions } from './whiteboardStatus'
import {
  ActionMenuEntry,
  ActionMenuSubheader,
} from '../components/datagrid/ActionMenu'
import { useNavigate } from 'react-router-dom'
import DueDateToolbar from './DueDateToolbar'
import { Button } from '@mui/material'
import DueDatePreview from './DueDatePreview'
import AlertDialog from '../components/AlertDialog'
import { useGlobalConfig } from '../hooks/useGlobalConfig'
import { isV2StationeryPreviewEnabled } from '../utils/stationery'
import PdfInlinePreviewDialog from '../components/pdf/PdfInlinePreviewDialog'
import LoadingSpinner from '../components/Loading'
import { buildDueDatePdfViaStationeryV2 } from './DueDatePdfV2Builder'
import { transformDueDateListParams } from './helpers'
import { format, isValid, parseISO } from 'date-fns'
import { daysRemainingUntilDue, isEmptyDueDate } from './dueDateDates'
const DueDateAdvancedSearchForm = lazy(() => import("./AdvanceSearchForm"));
interface DueDateDataGridProps extends AeronetViewProps{
  canCreateDuedate:boolean
canEditDuedate:boolean
canDeleteDuedate:boolean

}

const formatGridDate = (
  value: unknown,
  dateFormat: string | undefined
) => {
  if (isEmptyDueDate(value) || !dateFormat) return "";
  try {
    const parsed = parseISO(String(value));
    if (!isValid(parsed)) return "";
    return format(parsed, dateFormat);
  } catch {
    return "";
  }
};


const DueDateDataGrid: React.FC<DueDateDataGridProps>  = (props) => {
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
  const {canCreateDuedate, canEditDuedate, canDeleteDuedate} = props
    const dueDateConfig =useConfig("whiteboard")
    const globalConfig = useGlobalConfig();
    const useStationeryV2InlinePreview = isV2StationeryPreviewEnabled(globalConfig);
    const [previewSelected, setPreviewSelected] = useState<number>();
    const [previewSelectedStatus, setPreviewSelectedStatus] =
      useState<any>();
    const [previewLoading, setPreviewLoading] = useState(false);
    const [inlinePreviewOpen, setInlinePreviewOpen] = useState(false);
    const [inlinePreviewBlob, setInlinePreviewBlob] = useState<Blob | null>(null);
    const [inlinePreviewFilename, setInlinePreviewFilename] = useState("");
    const [tabDialogOpen, setTabDialogOpen] = useState(false);
    const [deletePayload, setDeletePayload] = useState<DueDateDeletePayload | null>(
      null
    );
    
    const [selectedRows, setSelectedRows] = useState<DueDateList[]>([]);
    const currentUser = useCurrentUser();
    const statusOptions = useMemo(() => whiteboardStatusOptions(dueDateConfig), [dueDateConfig]);
    const column:GridColDef[] = useMemo(()=>[
        {
            headerName:"ID",
            field:"id",
            flex:0.5
        },
        {
            headerName:"Title",
            field:"title",
            flex:1,
            filterable:false
        },
        {
            headerName:"Status",
            field:"status",
            flex:0.5,
            type: "singleSelect",
            filterable:true,
            valueOptions: statusOptions,
            valueFormatter: (params: GridValueFormatterParams) =>
              whiteboardStatusLabel(dueDateConfig, params.value),
            valueGetter: (params) => params.row?.status,
        },
        {
            headerName:"Section",
            field:"section",
            flex:0.75,
            filterable:false,
            sortable: false,
            valueGetter:(params)=> params.row?.section?.name
        },
        {
            headerName:"Type",
            field:"type_id",
            flex:0.75,
            filterable:false,
            sortable: false,
            valueGetter:(params)=> params.row?.type_obj?.name
        },
        {
            headerName:"Company",
            field:"company",
            flex:0.75,
            filterable:false,
            sortable: false,
            valueGetter:(params)=> params.row?.company_obj?.name ?? ""
        },
        {
            headerName:"Department",
            field:"department",
            flex:0.75,
            filterable:false,
            sortable: false,
            valueGetter:(params)=> params.row?.department_obj?.name ?? ""
        },
        {
            headerName:"Assigned To",
            field:"assigned_to",
            flex:0.75,
            filterable:false,
            sortable: false,
            valueGetter:(params)=>
              params.row?.assigned_obj?.name ??
              params.row?.assigned_obj?.gecos ??
              params.row?.user_obj?.name ??
              params.row?.user_obj?.gecos ??
              ""
        },
        {
            headerName:"Reference",
            field:"reference",
            flex:0.75,
            filterable:false,
            sortable: false,
        },
        {
            headerName:"Comment",
            field:"comment",
            flex:1,
            filterable:false
        },
        {
            headerName:"Last Done",
            field:"last_done",
            flex:0.75,
            filterable:false,
            valueFormatter: (params: GridValueFormatterParams) =>
              formatGridDate(params.value, globalConfig?.date_format),
        },
        {
            headerName:"Due Date",
            // `required` is the real column behind this date — on the model
            // (`WhiteBoard.required`) and on `DueDateList`. The field was
            // `due_date`, which exists in neither, so `AeronetDataGrid` sent
            // `?sort=due_date` on a header click and the whole list 422'd.
            // The valueGetter below keeps the `due_date` fallback for rows
            // that still carry it.
            field:"required",
            flex:0.75,
            filterable:false,
            valueGetter: (params) =>
              params.row?.required ?? params.row?.due_date ?? "",
            valueFormatter: (params: GridValueFormatterParams) =>
              formatGridDate(params.value, globalConfig?.date_format),
        },
        {
            headerName:"Remaining",
            field:"remaining",
            flex:0.5,
            filterable:false,
            sortable: false,
            align: "right",
            headerAlign: "right",
            valueGetter: (params) => {
              if (params.row?.remaining != null && params.row.remaining !== "") {
                return params.row.remaining;
              }
              return daysRemainingUntilDue(
                params.row?.required ?? params.row?.due_date ?? null
              );
            },
            valueFormatter: (params: GridValueFormatterParams) =>
              params.value == null || params.value === "" ? "" : String(params.value),
        },
    ],[dueDateConfig, statusOptions, globalConfig?.date_format])

    const getData = useCallback(
        async (params: any, axiosConfig?: CacheRequestConfig) => {
                
            // return {
            //     total:0,
            //     rows:[],
            //   }
          try {
            const apiParams = transformDueDateListParams(params);
            const resp: AxiosResponse =
              await axios.get("/v6/duedate", {
                params: apiParams,
                paramsSerializer: (params) => queryString.stringify(params),
                ...axiosConfig,
              });
              return {
                total: resp.data.total,
                rows: resp.data.data,
              } as QueryResults<DueDateList>;
          } catch (e) {
            // @ts-ignore
            if (!axios.isCancel(e)) {
              enqueueSnackbar("Error fetching Duedate", {
                variant: "error",
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "center",
                },
              });
            }
            throw e;
          }
        },
        [enqueueSnackbar])

      const deleteDuedate = useCallback(() => {
        const dueDateIds = selectedRows.map((row) => row.id);
        if (!dueDateIds || dueDateIds.length === 0) {
          return;
        }
        for (const id of dueDateIds) {
          axios
            .delete(`/v6/duedate/${id}`)
            .then(() => {
              enqueueSnackbar(`Duedate deleted ${id}`, {
                variant: "success",
              });
              if (deletePayload?.apiRef) {
                deletePayload.apiRef.current.updateRows([
                  { id: id, _action: "delete" },
                ]);
              }
            })
            .catch((error) => {
              console.error(error);
              enqueueSnackbar(`Error deleting Inspection`, {
                variant: "error",
              });
            });
        }
        deletePayload?.setSelectionModel([]);
        setDeletePayload(null);
        setSelectedRows([]);
      }, [deletePayload, enqueueSnackbar, selectedRows]);


    const openDueDateControlReportPreview = useCallback(
      (duedateId: number, status: number) => {
        const rowStatus = whiteboardStatusLabel(dueDateConfig, status);

        if (useStationeryV2InlinePreview) {
          setPreviewLoading(true);
          void (async () => {
            try {
              const { blob, filename } = await buildDueDatePdfViaStationeryV2({
                duedateId,
                dueDateConfig,
                status: rowStatus,
              });
              setInlinePreviewBlob(blob);
              setInlinePreviewFilename(filename);
              setInlinePreviewOpen(true);
            } catch {
              enqueueSnackbar(`Failed to render due date ${duedateId}`, {
                variant: "error",
              });
            } finally {
              setPreviewLoading(false);
            }
          })();
          return;
        }

        setPreviewSelectedStatus(rowStatus ?? status);
        setPreviewLoading(true);
        setPreviewSelected(duedateId);
      },
      [dueDateConfig, useStationeryV2InlinePreview, enqueueSnackbar]
    );

    const actions: ActionMenuEntry[] = useMemo(() => {
      const items: ActionMenuEntry[] = [];

      if (canEditDuedate) {
        items.push(
          {
            name: "Duplicate",
            namePlural: "Duplicate",
            action: ["Duplicate", "DUPLICATE"],
            requiresSelection: true,
          },
          {
            name: "Copy To User",
            namePlural: "Copy To User",
            action: ["Copy_To_User", "COPY_TO_USER"],
            requiresSelection: true,
          },
          {
            name: "Set Status To Open",
            namePlural: "Set Status To Open",
            action: ["Set_Status_To_Open", "SET_STATUS_TO_OPEN"],
            requiresSelection: true,
          },
          {
            name: "Set Status To Close",
            namePlural: "Set Status To Close",
            action: ["Set_Status_To_Close", "SET_STATUS_TO_CLOSE"],
            requiresSelection: true,
          }
        );
      }

      if (canDeleteDuedate) {
        items.push({
          name: "Delete",
          namePlural: "Delete",
          action: ["Delete", "DELETE"],
          requiresSelection: true,
          multipleSelection: true,
          onClick: async (event: DueDateDeletePayload) => {
            const rows = [...event.selectedRows].map((row) => row);

            setTabDialogOpen(true);
            setDeletePayload(event);
            setSelectedRows(rows);
          },
        });
      }

      items.push({
        subheader: "Print",
        children: [
          {
            name: "Due Date Control Report",
            requiresSelection: true,
            preventMultiSelect: true,
            multiSelectErrorMessage:
              "Select a single row to preview the due date control report.",
            isReturnDataSelection: true,
            onClick: (payload) => {
              const first = payload?.selectedRows?.next()?.value as
                | DueDateList
                | undefined;
              if (first) {
                openDueDateControlReportPreview(first.id, first.status);
              }
            },
          },
        ],
      } satisfies ActionMenuSubheader);

      return items;
    }, [canEditDuedate, canDeleteDuedate, openDueDateControlReportPreview]);

    const onRowClick = useCallback(
      (param: any) => {
        navigate(String(param.row.id));
      },
      [navigate]
    );


const advancedSearchDefaultItems = useMemo(
  () => [
    {
      id: 1,
      field: "company_id",
      operator: "is",
      value: "",
    },
    {
      id: 2,
      field: "status",
      operator: "is",
      value: "",
    },
    {
      id: 3,
      field: "type_id",
      operator: "is",
      value: "",
    },
    {
      id: 4,
      field: "section_id",
      operator: "is",
      value: "",
    },
    {
      id: 6,
      field: "assigned_to",
      operator: "is",
      value: "",
    },
    {
      id: 7,
      field: "supervisor",
      operator: "is",
      value: "",
    },
    {
      id: 8,
      field: "department",
      operator: "is",
      value: "",
    },
    {
      id: 5,
      field: "date_type",
      operator: "is",
      value: "",
    },
    {
      id: 10,
      field: "date_from",
      operator: "is",
      value: "",
    },
    {
      id: 11,
      field: "date_to",
      operator: "is",
      value: "",
    },
    {
      id: 9,
      field: "endorsments",
      operator: "is",
      value: "",
    },
  ],
  []
);

const onSearch = useCallback(
  (filterModel: GridFilterModel, apiRef: MutableRefObject<GridApi>) => {
    console.log("FilterModel....", filterModel);
    apiRef.current.setFilterModel(filterModel, "upsertFilterItems");
  },
  []
);


const quickSearchItems = useMemo(
  () => [
    {
      field: "id",
      operator: "startsWith",
      label: "ID",
    },
    {
      field: "title",
      operator: "contains",
      label: "Title",
    },
    {
      field: "comment",
      operator: "contains",
      label: "Comment",
    },
    {
      field: "reference",
      operator: "contains",
      label: "Reference",
    },
  ],
  []
);

const handleClose = useCallback(() => {
  setPreviewSelected(undefined);
  setPreviewLoading(false);
}, []);

const handleInlinePreviewClose = useCallback(() => {
  setInlinePreviewOpen(false);
  setInlinePreviewBlob(null);
  setInlinePreviewFilename("");
}, []);

const initialSort: GridSortItem[] = useMemo(
  () => [{ field: "id", sort: "desc" }],
  []
);


  return (
   <>
   <AeronetDataGrid
   advancedSearchForm={DueDateAdvancedSearchForm}
    advancedSearchProps={{
      items: advancedSearchDefaultItems,
      onSearch: onSearch,
    }}
    components={{
      toolbar:DueDateToolbar
    }}
    initialSort={initialSort}
    disableColumnFilter = {false}
    enableFilter = {true}
    columns={column}
    data={getData}
    name='DueDate-DataGrid'
      checkboxSelection
    enableCreate={canCreateDuedate}
    currentUserId={currentUser?.id}
    actions={actions}
    onRowClick = {onRowClick}
    quickSearchItems={quickSearchItems}
   />
   {previewSelected ? (
        <DueDatePreview
        duedateId={previewSelected}
          status={previewSelectedStatus}
          onClose={handleClose}
          onReady={() => setPreviewLoading(false)}
        />
      ) : null}
      <LoadingSpinner open={previewLoading} />
      <PdfInlinePreviewDialog
        open={inlinePreviewOpen}
        blob={inlinePreviewBlob}
        filename={inlinePreviewFilename}
        title="Due Date Preview"
        onClose={handleInlinePreviewClose}
      />
      <AlertDialog
        title="Delete this record?"
        open={tabDialogOpen}
        handleClose={() => {
          setTabDialogOpen(false);
        }}
      >
        <Button
          onClick={() => {
            setTabDialogOpen(false);
          }}
          color="primary"
        >
          No
        </Button>
        <Button
          onClick={() => {
            setTabDialogOpen(false);
            deleteDuedate();
          }}
          color="primary"
          autoFocus
        >
          Yes
        </Button>
      </AlertDialog>
   </>
  )
}

export default DueDateDataGrid
